/**
 * Source: public/design-patterns.html F-1a and docs/structure/03_詳細設計_文章正典と導線.md C-3.
 * 2026-09-17 作り直し v2(Yuichi 再指摘: 見た目を旅執事 /contact/ に合わせる・ご用件はカードで切替・
 * カードを選ぶまで項目を出さない・「ご希望の連絡方法」は無し)。
 * 先例: 旅執事 src/pages/contact/page.tsx(contact-method カード + 入力欄の形・余白・密度)。
 * 実測値は docs/_qa/_form_research_tabishitsuji.md(見た目の対応表)。
 * 項目定義は src/data/forms.ts(データ駆動)。送信先の扱い(utils/formSubmit.ts・GAS)は従来どおり。
 */
import { type CSSProperties, type FormEvent, type ReactElement, useId, useRef, useState } from 'react'
import { submitForm, type FormSubmitAttachment, type FormSubmitField } from '../../utils/formSubmit'
import { useLocation } from 'react-router-dom'
import { commonHead, commonMessage, topics, variants, variantFromService, type FormFieldDef, type FormVariant, type TopicKey } from '../../data/forms'
import { formUi, localizeField, localizeTopic } from '../../data/forms.en'
import { getLang, type Lang } from '../../lib/lang'

type LegacyField = FormFieldDef
type Props = {
  service?: string
  /** 本文 JSON の form ブロックが持つ variant(salon/studio/apprentice/shop/global/recruit)。無い場合は service から導く */
  variant?: string
  /** 旧キー(後方互換のため受けるだけ。項目は forms.ts が正) */
  fields?: Array<LegacyField | string>
  extraFields?: LegacyField[]
  heading?: string
  submitLabel?: string
}

const MAX_FILES = 3 // 取材・資料(media.attachments・複数選択)の上限
const MAX_FILE_BYTES = 8 * 1024 * 1024 // 1 ファイル 8MB(THE BUTLER 応募フォームと同じ)
const MAX_TOTAL_BYTES = 25 * 1024 * 1024 // 合計 25MB(GAS 側の上限)
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}

const honeypotStyle = { position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' } satisfies CSSProperties

/** 旅執事は名前/メールを2列に並べる(page.tsx:167)。同じ密度で対にする項目 */
const PAIRS: ReadonlyArray<readonly [string, string]> = [['name', 'furigana'], ['email', 'tel']]

function resolveVariant(variant?: string, service?: string): FormVariant {
  if (variant && variant in variants) return variant as FormVariant
  return variantFromService(service)
}

function Control({ field, id, t }: { field: FormFieldDef; id: string; t: typeof formUi.ja | typeof formUi.en }) {
  const common = { id, name: field.name, required: field.required, placeholder: field.placeholder, 'aria-required': field.required || undefined }
  if (field.type === 'textarea') return <textarea {...common} rows={field.name === 'message' ? 6 : 3} />
  if (field.type === 'select') return (
    <span className="qbf-sel">
      <select {...common} defaultValue="">
        <option value="" disabled={field.required}>{field.required ? t.selectRequired : t.selectNone}</option>
        {field.options?.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </span>
  )
  if (field.type === 'checkbox') return <input {...common} type="checkbox" />
  // 複数選択のチェック群(保有スキル・資格)。value は表示ラベル。THE BUTLER の skillgrid と同じ並び
  if (field.type === 'checkgroup') return (
    <span className="qbf-checks" role="group" aria-labelledby={`${id}-lb`}>
      {field.options?.map((option, index) => <label key={option} className="qbf-check"><input type="checkbox" name={field.name} value={option} id={index === 0 ? id : undefined} /><span>{option}</span></label>)}
    </span>
  )
  // 応募書類(履歴書等)は 1 項目 1 ファイル、取材資料(media.attachments)は複数
  if (field.type === 'file') return <input {...common} type="file" multiple={field.name === 'attachments'} accept={field.accept} className="qbf-file" />
  return <input {...common} type={field.type ?? 'text'} step={field.type === 'number' ? 'any' : undefined} inputMode={field.type === 'tel' ? 'tel' : field.type === 'number' ? 'decimal' : undefined} autoComplete={field.name === 'email' ? 'email' : field.name === 'tel' ? 'tel' : field.name === 'name' ? 'name' : undefined} />
}

function Row({ field, id, t }: { field: FormFieldDef; id: string; t: typeof formUi.ja | typeof formUi.en }) {
  return (
    <div className="qbf-row">
      <label htmlFor={id} id={`${id}-lb`}>
        <span className="lb">{field.label}</span>
        {field.required ? <i className="req">{t.required}</i> : <i className="opt">{t.optional}</i>}
      </label>
      <Control field={field} id={id} t={t} />
      {field.hint && <span className="qbf-hint">{field.hint}</span>}
    </div>
  )
}

/** 旅執事の2列グリッド(name/email)と同じ並び。対になる項目が揃わない場合は1列 */
function Rows({ fields, uid, t }: { fields: FormFieldDef[]; uid: string; t: typeof formUi.ja | typeof formUi.en }) {
  const out: ReactElement[] = []
  const done = new Set<string>()
  for (const field of fields) {
    if (done.has(field.name)) continue
    const pair = PAIRS.find(([a, b]) => a === field.name && fields.some((f) => f.name === b))
    if (pair) {
      const second = fields.find((f) => f.name === pair[1])!
      done.add(pair[0]); done.add(pair[1])
      out.push(
        <div className="qbf-two" key={pair.join('-')}>
          <Row field={field} id={`${uid}-${field.name}`} t={t} />
          <Row field={second} id={`${uid}-${second.name}`} t={t} />
        </div>,
      )
      continue
    }
    done.add(field.name)
    out.push(<Row key={field.name} field={field} id={`${uid}-${field.name}`} t={t} />)
  }
  return <>{out}</>
}

export function Form({ service = 'common', variant, heading, submitLabel }: Props) {
  const uid = useId()
  // 言語は pathname から判定(SSG のため window 参照禁止)。文言は forms.en.ts、項目は forms.ts(JA)に英語を重ねる
  const { pathname } = useLocation()
  const lang: Lang = getLang(pathname)
  const t = formUi[lang]
  const variantKey = resolveVariant(variant, service)
  const def = variants[variantKey]
  /** 初期状態は未選択(SSG でも同じ)。カードを選ぶまで入力欄を一切出さない */
  const [topicKey, setTopicKey] = useState<TopicKey | null>(null)
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'toolarge'>('idle')
  const sendingRef = useRef(false)
  const fieldsRef = useRef<HTMLFieldSetElement | null>(null)
  function selectTopic(key: TopicKey) {
    setTopicKey(key)
    setStatus('idle')
    requestAnimationFrame(() => fieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  const topic = topicKey ? localizeTopic(topics[topicKey], lang) : null
  const head = commonHead.filter((field) => !def.omitCommon?.includes(field.name)).map((field) => localizeField(field, lang))
  const messageBase = localizeField(commonMessage, lang)
  const message: FormFieldDef | null = topic ? { ...messageBase, label: topic.messageLabel ?? messageBase.label, placeholder: topic.messagePlaceholder ?? messageBase.placeholder, required: topic.messageRequired ?? false } : null
  const visible: FormFieldDef[] = topic && message ? [...head, ...topic.fields, message] : []

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (sendingRef.current || !topic || !topicKey) return
    const form = event.currentTarget
    const data = new FormData(form)
    // 添付はフォーム全体で 3 点・合計 10MB。超過は黙って捨てず、エラー表示で止める(Grok レビュー 2026-09-17 指摘)
    const fileFields = visible.filter((f) => f.type === 'file')
    const files = fileFields.flatMap((f) => Array.from(data.getAll(f.name)).filter((x): x is File => x instanceof File && x.size > 0).map((file) => ({ field: f.name, file })))
    const maxFiles = Math.max(MAX_FILES, fileFields.length) // 応募は書類 4 点、取材は資料 3 点まで
    if (files.length > maxFiles || files.some(({ file }) => file.size > MAX_FILE_BYTES) || files.reduce((n, { file }) => n + file.size, 0) > MAX_TOTAL_BYTES) { setStatus('toolarge'); return }
    sendingRef.current = true
    setStatus('sending')
    const attachments: FormSubmitAttachment[] = []
    try {
      for (const { field, file } of files) attachments.push({ field, name: file.name, mimeType: file.type || 'application/octet-stream', data: await toBase64(file) })
    } catch {
      sendingRef.current = false
      setStatus('error')
      return
    }
    const payloadFields: FormSubmitField[] = [
      { name: 'subject', label: t.subject, value: topic.label },
      ...visible.map((field) => ({
        name: field.name,
        label: field.label,
        // checkgroup は選択ラベルを「 / 」で 1 行に(THE BUTLER RecruitEntryPage.tsx 176 行の方式)。file はその項目の添付ファイル名
        value: field.type === 'checkbox' ? (data.has(field.name) ? t.yes : '') : field.type === 'checkgroup' ? data.getAll(field.name).map(String).join(' / ') : field.type === 'file' ? attachments.filter((a) => a.field === field.name).map((a) => a.name).join(', ') : String(data.get(field.name) ?? ''),
      })),
    ]
    try {
      await submitForm({
        formType: topicKey === 'recruit' ? 'recruit' : 'contact',
        topic: topicKey, // GAS 側の宛先振り分け(salon@/studio@/footman@/shop@/recruit@/info@)
        fields: payloadFields,
        attachments,
        lang,
        page: location.pathname,
        _hp: String(data.get('website') ?? ''),
      })
      setStatus('sent')
      form.reset()
    } catch {
      setStatus('error')
    } finally {
      sendingRef.current = false
    }
  }

  const cardsId = `${uid}-cards`
  return (
    <div className="shell sec tight qbf-wrap" data-form-variant={variantKey}>
      <div className="qbf">
        <div className="qbf-head">
          <span className="qbf-eyebrow">{t.eyebrow}</span>
          {heading && <h2 className="qbf-heading">{heading}</h2>}
        </div>

        {status === 'sent' ? (
          <div className="qbf-sent" role="status">
            <span className="qbf-sent-ic" aria-hidden="true">✓</span>
            <div>
              <p className="t">{t.sentT}</p>
              <p className="d">{t.sentD}</p>
            </div>
          </div>
        ) : (
          <form className="qbf-form" onSubmit={handleSubmit} aria-describedby={cardsId}>
            <ol className="qbf-steps" aria-label={t.stepsLabel}>
              <li className={topic ? 'done' : 'now'} aria-current={topic ? undefined : 'step'}><b>1</b><span>{t.step1}</span></li>
              <li className={topic && status !== 'sending' ? 'now' : topic ? 'done' : ''} aria-current={topic && status !== 'sending' ? 'step' : undefined}><b>2</b><span>{t.step2}</span></li>
              <li className={status === 'sending' ? 'now' : ''} aria-current={status === 'sending' ? 'step' : undefined}><b>3</b><span>{t.step3}</span></li>
            </ol>
            <div className="qbf-pick">
              <div className="qbf-step-head" id={cardsId}>
                <span className="num" aria-hidden="true">1</span>
                <h3>{t.pickH}<i className="req">{t.required}</i></h3>
                <p>{t.pickP}</p>
              </div>
              <div className="qbf-cards" role="group" aria-label={lang === 'en' ? t.subjectLabel : def.subjectLabel}>
                {def.topics.map((key) => {
                  const tp = localizeTopic(topics[key], lang)
                  const on = key === topicKey
                  return (
                    <button type="button" key={key} className={`qbf-cardbtn${on ? ' on' : ''}`} aria-pressed={on} onClick={() => selectTopic(key)} data-topic={key}>
                      <span className="chk" aria-hidden="true">{on ? '✓' : ''}</span>
                      <span className="ic" aria-hidden="true">{tp.en.slice(0, 1)}</span>
                      <span className="en">{tp.en}</span>
                      <span className="ttl">{tp.label.replace(/\s?\(.*\)$/, '')}</span>
                      <span className="tx">{tp.card}</span>
                      <span className="sel" aria-hidden="true">{on ? t.selected : t.choose}</span>
                    </button>
                  )
                })}
              </div>
              {topic?.lead && <p className="qbf-lead">{topic.lead}</p>}
            </div>

            {/* Yuichi 2026-09-17「②ご入力くださいませ は…見出しは残しておけ」「フォーム表示部分は①を選択してくださいとメッセージを出しておく」: STEP2 の見出しは常時表示、未選択時は案内のみ */}
            {!(topic && topicKey && message) && (
              <div className="qbf-fields qbf-fields-empty" aria-live="polite">
                <div className="qbf-step-head">
                  <span className="num" aria-hidden="true">2</span>
                  <h3>{t.enterH}</h3>
                  <p>{t.enterEmptyP}</p>
                </div>
                <p className="qbf-placeholder">{t.placeholder}</p>
              </div>
            )}
            {topic && topicKey && message && (
              <fieldset className="qbf-fields" key={topicKey} data-topic-fields={topicKey} ref={fieldsRef}>
                <legend className="sr-only">{topic.label}</legend>
                <div className="qbf-step-head">
                  <span className="num" aria-hidden="true">2</span>
                  <h3>{t.enterH}</h3>
                  <p>{t.enterP}</p>
                </div>
                <div className="qbf-selected" role="status">
                  <span className="ic" aria-hidden="true">{topic.en.slice(0, 1)}</span>
                  <span className="tx"><small>{t.selectedTopic}{topic.en}</small><strong>{topic.label}</strong></span>
                  <button type="button" className="chg" onClick={() => { setTopicKey(null); setStatus('idle') }}>{t.change}</button>
                </div>
                {status === 'error' && <p className="qbf-err" role="alert">{t.error}</p>}
                {status === 'toolarge' && <p className="qbf-err" role="alert">{t.tooLarge}</p>}

                <Rows fields={head} uid={uid} t={t} />
                {topic.fields.length > 0 && <Rows fields={topic.fields} uid={uid} t={t} />}
                <Row field={message} id={`${uid}-message`} t={t} />

                <input type="text" name="website" aria-hidden="true" tabIndex={-1} autoComplete="off" style={honeypotStyle} />

                <label className="qbf-agree">
                  <input type="checkbox" name="terms" required />
                  <span>{t.agreePre}<a href={t.termsHref}>{t.terms}</a>{t.sep}<a href={t.privacyHref}>{t.privacy}</a>{topic.cancelUrl && <>{t.sep}<a href={topic.cancelUrl}>{t.cancel}</a></>}{t.agreePost}</span>
                </label>

                <button className="qbf-submit" type="submit" disabled={status === 'sending'}>
                  {status === 'sending' ? t.sending : (submitLabel ?? t.submit)}
                  {status !== 'sending' && <span aria-hidden="true">→</span>}
                </button>
                <p className="qbf-after">{t.after1}<a href="mailto:info@queensbutler.jp">info@queensbutler.jp</a>{t.after2}</p>
              </fieldset>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
